import TosPdfViewerModal from '@/components/modals/TosPdfViewerModal';
import { SessionContext } from '@/context/SessionContext';
import ConductionAPI from '@/javascript/conduction-api';
import { Utils } from '@/javascript/utils';
import { Button, Card, Checkbox, Flex, Group, Loader, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconAt, IconLock, IconUser } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const UserRegistrationModal: React.FC = () => {
	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("Component must be a child of sessionContext.");
	}

	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [tosViewerOpen, setTosViewerOpen] = useState(false);

	const form = useForm({
		initialValues: {
			username: '',
			email: '',
			firstName: '',
			lastName: '',
			password: '',
			confirmPassword: '',
			tosAgreed: false
		},
		validate: {
			username: (value: string) => (value.trim().length === 0 ? 'Username is required' : null),
			email: (value: string) => !/^\S+@\S+\.\S+$/.test(value) ? 'Invalid email address' : null,
			firstName: (value: string) => (value.trim().length === 0 ? 'First name is required' : null),
			lastName: (value: string) => (value.trim().length === 0 ? 'Last name is required' : null),
			password: (value: string) => {
				if (value.length < 6 || value.length > 30) return 'Password must be between 6 and 30 characters';
				if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
				if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
				if (!/[0-9]/.test(value)) return 'Password must contain a number';
				if (!/[^a-z\d]/i.test(value)) return 'Password must contain a special character';
				return null;
			},
			confirmPassword: (value: any, values: { password: any; }) =>
				value !== values.password ? 'Passwords do not match' : null,
			tosAgreed: (value: boolean) => (!value ? 'You must agree to the Terms of Service to register an account.' : null),
		},
	});

	const handleRegister = async () => {
		const notificationId = Utils.displayNotification("Creating Account", "This may take a few moments...", "blue", "loading");

		setIsSubmitting(true);
		try {
			let registrationResult = await ConductionAPI.registerUser(
				form.values.username,
				form.values.email,
				form.values.firstName,
				form.values.lastName,
				form.values.password,
				form.values.confirmPassword
			);

			if (registrationResult) {
				Utils.updateNotification(notificationId, "Account Created", null, "green", <IconUser size={16} />);

				// Log the user in automatically
				const automaticallyAgreeTOS = session.currentTos != null && form.values.tosAgreed;
				if (await session.login(form.values.username, form.values.password, false, automaticallyAgreeTOS)) {
					navigate("/")
				}
			} else {
				Utils.updateNotification(notificationId, "Username or Email Taken", "The entered username or email may already be in use. Try a different one.", "red", <IconAlertCircle size={16} />, true, 10000);
			}
		} catch (error) {
			Utils.updateNotification(notificationId, "Registration Error", "An error occurred during account creation. Please try again later.", "red", <IconAlertCircle size={16} />, true, false);
		} finally {
			setIsSubmitting(false);
		}
	};

	useEffect(() => {
		if (session.currentTos == null) {
			session.fetchCurrentTos();
		}
	}, [session.user?.agreedToCurrentTos, session.currentTos]);

	return (
		<Flex
			w="100%"
			h="100%"
			justify="center"
			align="center"
		>
			<Card
				w="60%"
				miw="500px"
			>
				<Stack>
					<Title order={2} mb={20}>Create a Conduction Account</Title>

					<form onSubmit={form.onSubmit(handleRegister)}>
						<Stack>
							<TextInput
								label={
									<Group>
										<IconUser size={20} />
										<Text>Username</Text>
										<Text c="dimmed" fz="sm">(must be unique, used for signing in)</Text>
									</Group>
								}
								{...form.getInputProps('username')}
								required
								withAsterisk={false}
								disabled={isSubmitting}
							/>

							<TextInput
								label={
									<Group>
										<IconAt size={20} />
										<Text>Email</Text>
										<Text c="dimmed" fz="sm">(for notifications about the music videos we will record for you)</Text>
									</Group>
								}
								{...form.getInputProps('email')}
								required
								withAsterisk={false}
								disabled={isSubmitting}
							/>

							<Group
								grow
							>
								<TextInput
									label={
										<Group>
											<IconUser size={20} />
											<Text>First Name</Text>
										</Group>
									}
									{...form.getInputProps('firstName')}
									required
									withAsterisk={false}
									disabled={isSubmitting}
								/>

								<TextInput
									label={
										<Group>
											<Text>Last Name</Text>
										</Group>
									}
									{...form.getInputProps('lastName')}
									required
									withAsterisk={false}
									disabled={isSubmitting}
								/>
							</Group>

							<PasswordInput
								label={
									<Group>
										<IconLock size={20} />
										<Text>Password</Text>
									</Group>
								}
								{...form.getInputProps('password')}
								required
								withAsterisk={false}
								description="At least 6 characters, including uppercase, lowercase, a number, and a special character."
								disabled={isSubmitting}
							/>

							<PasswordInput
								label={
									<Group>
										<IconLock size={20} />
										<Text>Confirm Password</Text>
									</Group>
								}
								{...form.getInputProps('confirmPassword')}
								required
								withAsterisk={false}
								disabled={isSubmitting}
							/>

							{session.currentTos == null ? (
								<Loader />
							) : (
								<Stack>
									<Text>
										Please review our current Terms of Service.
									</Text>

									<TosPdfViewerModal
										opened={tosViewerOpen}
										onClose={() => setTosViewerOpen(false)}
									/>

									<Group>
										<Button variant="light" onClick={() => setTosViewerOpen(true)}>
											View Terms
										</Button>

										<Checkbox
											label="I agree to the Terms of Service"
											{...form.getInputProps('tosAgreed', { type: 'checkbox' })}
											disabled={isSubmitting || session.currentTos == null}
											required
										/>
									</Group>
								</Stack>
							)}

							<Button
								type="submit"
								radius="xl"
								size='sm'
								loading={isSubmitting}
							>
								Register
							</Button>
						</Stack>
					</form>
				</Stack>
			</Card>
		</Flex>
	);
};

export default UserRegistrationModal;
